import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Send, Loader2, Sparkles, Users, Megaphone, Lightbulb } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Input, Label } from "../components/ui/Input";
import Button from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";

const CollabPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    subject: "",
    website: "",
    description: "",
    audience: "",
    proposal: "",
    category: "mutual-promotion",
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert(t("collab.loginAlert"));
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API}/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: formData.subject,
          body: `
${t("collab.bodySite")}
${formData.website}

${t("collab.bodyDescription")}
${formData.description}

${t("collab.bodyAudience")}
${formData.audience}

${t("collab.bodyProposal")}
${formData.proposal}
          `.trim(),
          category: formData.category,
          ticket_type: "collaboration",
        }),
      });

      if (!response.ok) {
        let errorMessage = t("collab.errorSubmit");
        try {
          const errorData = await response.json();
          if (errorData?.detail) {
            errorMessage =
              typeof errorData.detail === "string"
                ? errorData.detail
                : JSON.stringify(errorData.detail);
          }
        } catch {
          // ignore
        }
        alert(`${t("collab.errorPrefix")} ${errorMessage}`);
        return;
      }

      setFormData({
        subject: "",
        website: "",
        description: "",
        audience: "",
        proposal: "",
        category: "mutual-promotion",
      });
      alert(t("collab.sent"));
    } catch (error) {
      console.error("Collab submit error:", error);
      alert(t("collab.networkError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-20 px-6 pt-32">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h1 className="font-display text-5xl md:text-6xl text-gold mb-4" data-testid="collab-title">
            {t("collab.title")}
          </h1>

          <p className="font-serif-fantasy italic text-lg md:text-xl text-parchment/90 max-w-3xl mx-auto mb-14">
            {t("collab.quote")}
          </p>

          <div className="flex justify-center mb-10">
            <div className="animate-float">
              <div className="rune-border p-3 rounded-3xl bg-card/50 backdrop-blur gold-glow">
                <img
                  src="https://assets.mytharion.app/Collab.png"
                  alt="Mytharion Emblem"
                  className="w-64 h-64 md:w-80 md:h-80 object-contain animate-arcane-glow"
                />
              </div>
            </div>
          </div>

          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("collab.intro")}{" "}
            <strong className="text-foreground">{t("collab.introStrong")}</strong>
            {t("collab.introTail")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <Card className="transition-all duration-700 ease-out hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.9)] hover:scale-[1.02]">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-gold" />
                  {t("collab.networkTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {t("collab.networkP1")}
                </p>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  {t("collab.networkP2")}
                </p>
              </CardContent>
            </Card>

            <Card className="transition-all duration-700 ease-out hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.9)] hover:scale-[1.02]">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Lightbulb className="w-5 h-5 mr-2 text-gold" />
                  {t("collab.howTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {[1, 2, 3].map((n) => (
                    <div className="flex gap-4" key={n}>
                      <div className="w-9 h-9 shrink-0 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold font-semibold">
                        {n}
                      </div>
                      <div>
                        <h3 className="font-heading text-foreground mb-1">
                          {t(`collab.step${n}Title`)}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t(`collab.step${n}Desc`)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-6 transition-all duration-700 ease-out hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.9)] hover:scale-[1.02]">
              <div className="flex items-center gap-3 mb-4">
                <Megaphone className="w-5 h-5 text-gold" />
                <h3 className="font-heading text-lg text-gold">{t("collab.typesTitle")}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {t("collab.typesDesc")}
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>{t("collab.type1")}</li>
                <li>{t("collab.type2")}</li>
                <li>{t("collab.type3")}</li>
                <li>{t("collab.type4")}</li>
                <li>{t("collab.type5")}</li>
                <li>{t("collab.type6")}</li>
              </ul>
            </div>
          </div>

          <Card className="h-fit transition-all duration-700 ease-out hover:drop-shadow-[0_0_10px_rgba(255,215,100,0.8)] hover:scale-[1.02]">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-gold" />
                {t("collab.formTitle")}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">{t("collab.formSubtitle")}</p>
            </CardHeader>
            <CardContent>
              {!user && (
                <div className="mb-6 p-4 bg-gold/10 border border-gold/30 rounded-lg text-sm text-muted-foreground">
                  <span className="text-gold font-heading">{t("collab.formLoginPromptCta")}</span>
                  {t("collab.formLoginPromptSuffix")}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>{t("collab.projectName")}</Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t("collab.projectNamePh")}
                    required
                    className="mt-2"
                    data-testid="collab-name"
                  />
                </div>

                <div>
                  <Label>{t("collab.website")}</Label>
                  <div className="relative mt-2">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="pl-9"
                      data-testid="collab-website"
                    />
                  </div>
                </div>

                <div>
                  <Label>{t("collab.descriptionField")}</Label>
                  <textarea
                    name="description"
                    rows={5}
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder={t("collab.descriptionPh")}
                    className="mt-2 w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                    data-testid="collab-description"
                  />
                </div>

                <div>
                  <Label>{t("collab.audienceField")}</Label>
                  <textarea
                    name="audience"
                    rows={3}
                    value={formData.audience}
                    onChange={handleChange}
                    placeholder={t("collab.audiencePh")}
                    className="mt-2 w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                    data-testid="collab-audience"
                  />
                </div>

                <div>
                  <Label>{t("collab.proposalField")}</Label>
                  <textarea
                    name="proposal"
                    rows={5}
                    value={formData.proposal}
                    onChange={handleChange}
                    required
                    placeholder={t("collab.proposalPh")}
                    className="mt-2 w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                    data-testid="collab-proposal"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={submitting || !user}
                  data-testid="collab-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("collab.sending")}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t("collab.submit")}
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  {t("collab.disclaimer")}
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CollabPage;
